import type {
  CalendarEvent,
  Contact,
  FollowUpLog,
  Note,
  SupervisorReminder,
  Task,
  Uuid,
} from '@/data/db';
import { noteDisplayTitle } from '@/features/notes/note-content';
import { stickerName } from '@/components/stickers/sticker-library';
import { STATUS_LABELS } from '@/features/tasks/task-presentation';

/**
 * 全域搜尋（spec 18）。
 *
 * 採記憶體內線性掃描（data-model 9.2）：2,000 筆的規模下，
 * 掃一遍的延遲遠低於一次 IndexedDB 往返，不需要引入搜尋套件。
 */

export type SearchGroupId =
  'tasks' | 'notes' | 'followUpLogs' | 'reminders' | 'contacts' | 'events';

export const SEARCH_GROUP_LABELS: Record<SearchGroupId, string> = {
  tasks: '任務與便條',
  notes: '隨手筆記',
  followUpLogs: '追蹤紀錄',
  reminders: '主管提醒',
  contacts: '聯絡人',
  events: '行事曆',
};

export interface SearchItem {
  id: string;
  title: string;
  meta: string;
  /** 點擊後要開啟的任務；筆記、聯絡人與獨立事件為 null。 */
  taskId: Uuid | null;
  inTrash: boolean;
}

export interface SearchGroup {
  id: SearchGroupId;
  label: string;
  items: SearchItem[];
}

export interface SearchableData {
  tasks: readonly Task[];
  contacts: readonly Contact[];
  notes: readonly Note[];
  followUpLogs: readonly FollowUpLog[];
  reminders: readonly SupervisorReminder[];
  events: readonly CalendarEvent[];
}

export interface SearchOptions {
  /** 垃圾桶內容預設排除（spec 18）。 */
  includeTrash?: boolean;
}

function normalize(value: string): string {
  return value.toLowerCase();
}

function haystack(...parts: Array<string | null | undefined>): string {
  return normalize(parts.filter((part): part is string => Boolean(part)).join('\n'));
}

export function searchEverything(
  keyword: string,
  data: SearchableData,
  options: SearchOptions = {},
): SearchGroup[] {
  const needle = normalize(keyword.trim());
  if (!needle) return [];

  const includeTrash = options.includeTrash ?? false;
  const keep = (deletedAt: string | null) => includeTrash || deletedAt === null;

  const groups: SearchGroup[] = [];
  const push = (id: SearchGroupId, items: SearchItem[]) => {
    if (items.length > 0) groups.push({ id, label: SEARCH_GROUP_LABELS[id], items });
  };

  push(
    'tasks',
    data.tasks
      .filter((task) => keep(task.deletedAt))
      .filter((task) =>
        haystack(
          task.subject,
          task.description,
          task.topic,
          ...task.stickers.map(stickerName),
        ).includes(needle),
      )
      .map((task) => ({
        id: task.id,
        title: task.subject,
        meta: [
          `#${task.number}`,
          task.kind === 'note' ? '便條' : '工作',
          STATUS_LABELS[task.status],
          task.archivedAt ? '已封存' : null,
          task.removedFromBoardAt ? '已從看板移除' : null,
        ]
          .filter(Boolean)
          .join('・'),
        taskId: task.id,
        inTrash: task.deletedAt !== null,
      })),
  );

  // 筆記比對的是寫入時就導出的 plainText，不是即時走一遍 ProseMirror JSON。
  push(
    'notes',
    data.notes
      .filter((note) => keep(note.deletedAt))
      .filter((note) =>
        haystack(note.customTitle ?? note.title, note.title, note.plainText).includes(needle),
      )
      .map((note) => ({
        id: note.id,
        title: noteDisplayTitle(note.title, note.customTitle),
        meta: firstDifferentLine(note.plainText, note.title),
        taskId: null,
        inTrash: note.deletedAt !== null,
      })),
  );

  push(
    'followUpLogs',
    data.followUpLogs
      .filter((log) => keep(log.deletedAt))
      .filter((log) => haystack(log.summary, log.response).includes(needle))
      .map((log) => ({
        id: log.id,
        title: log.summary,
        meta: [log.occurredAtLocal.replace('T', ' '), log.response ? '有回覆' : null]
          .filter(Boolean)
          .join('・'),
        taskId: log.taskId,
        inTrash: log.deletedAt !== null,
      })),
  );

  // 提醒沒有自己的 deletedAt，刪除是從母任務連動過來的，所以要回頭看任務。
  const taskDeletedAt = new Map(data.tasks.map((task) => [task.id, task.deletedAt]));

  push(
    'reminders',
    data.reminders
      .filter((current) => keep(taskDeletedAt.get(current.taskId) ?? null))
      .filter((current) => haystack(current.message).includes(needle))
      .map((current) => ({
        id: current.id,
        title: current.message,
        meta: [
          current.remindAtLocal.replace('T', ' '),
          current.state === 'done' ? '已提醒' : '待提醒',
        ]
          .filter(Boolean)
          .join('・'),
        taskId: current.taskId,
        inTrash: (taskDeletedAt.get(current.taskId) ?? null) !== null,
      })),
  );

  push(
    'contacts',
    data.contacts
      .filter((current) => keep(current.deletedAt))
      .filter((current) =>
        haystack(
          current.name,
          current.organization,
          current.department,
          current.jobTitle,
          current.note,
        ).includes(needle),
      )
      .map((current) => ({
        id: current.id,
        title: current.name,
        meta: [current.jobTitle, current.organization, current.department]
          .filter(Boolean)
          .join('・'),
        taskId: null,
        inTrash: current.deletedAt !== null,
      })),
  );

  push(
    'events',
    data.events
      .filter((current) => keep(current.deletedAt))
      .filter((current) => haystack(current.title, current.note).includes(needle))
      .map((current) => ({
        id: current.id,
        title: current.title,
        meta: [
          current.startAtLocal.slice(0, 10),
          current.allDay ? '全天' : current.startAtLocal.slice(11),
        ].join('・'),
        taskId: current.taskId,
        inTrash: current.deletedAt !== null,
      })),
  );

  return groups;
}

/**
 * 筆記的第二行摘要。標題本來就取自第一行，再重複一次沒有資訊量，
 * 所以跳過與標題相同的那一行。
 */
function firstDifferentLine(plainText: string, title: string): string {
  const line = plainText.split('\n').find((candidate) => candidate.trim() !== title.trim());
  return (line ?? '').trim().slice(0, 60);
}
